import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
  DocumentReference,
  writeBatch,
  collectionGroup,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase';
import { Receipt } from '../types';
import { recalculateAndUpdateUserBalance, recalculateAndUpdateUserOutstandingAndCredits } from './userService';
import { updateOrgStats, addDebitItem, removeDebitItem, updateDebitItem, addCreditItem } from './statsService';

export const createReceipt = async (
  userId: string,
  receiptData: Omit<Receipt, 'id' | 'createdAt' | 'updatedAt' | 'userRef'>
): Promise<Receipt> => {
  try {
    console.log('🎆 createReceipt: Starting with userId:', userId);
    console.log('🎆 createReceipt: Receipt data:', receiptData);
    
    const userRef = doc(db, 'users', userId);
    const receiptsCollection = collection(userRef, 'receipts');
    const receiptRef = doc(receiptsCollection);
    const now = Timestamp.now();
    
    console.log('📁 createReceipt: Created references:', {
      userRefPath: userRef.path,
      receiptRefPath: receiptRef.path,
      receiptId: receiptRef.id
    });
    
    const receipt: Receipt = {
      ...receiptData,
      id: receiptRef.id,
      userRef,
      status: 'active', // Default status for new receipts
      createdAt: now,
      updatedAt: now
    };
    
    console.log('📦 createReceipt: Final receipt object:', receipt);
    console.log('💾 createReceipt: Saving to Firestore...');
    
    await setDoc(receiptRef, receipt);
    console.log('✅ receiptService: Receipt created successfully:', receiptRef.id, 'for user:', userId);
    console.log('📍 receiptService: Receipt saved at path:', receiptRef.path);
    
    // Update user balance after creating receipt
    try {
      await recalculateAndUpdateUserBalance(userId, receiptData.organizationId);
      await recalculateAndUpdateUserOutstandingAndCredits(userId, receiptData.organizationId);
      console.log('💰 receiptService: User balance and outstanding/credits updated after receipt creation');
    } catch (balanceError) {
      console.error('❌ receiptService: Error updating user balance after receipt creation:', balanceError);
      // Don't throw error here to avoid failing receipt creation
    }
    
    return receipt;
  } catch (error) {
    console.error('receiptService: Error creating receipt:', error);
    throw error;
  }
};

export const createDebitReceipt = async (
  userRef: DocumentReference,
  productRef: DocumentReference,
  productInfo: { name: string; price: number; invoiceDate?: Date; deadline?: Date; originalPrice?: number; discountApplied?: string },
  organizationId: string,
  academyId?: string
): Promise<Receipt> => {
  try {
    console.log('🧾 createDebitReceipt: Starting with:', {
      userRefId: userRef.id,
      userRefPath: userRef.path,
      productRefId: productRef.id,
      productInfo,
      organizationId,
      academyId
    });

    const invoiceDate = productInfo.invoiceDate 
      ? Timestamp.fromDate(productInfo.invoiceDate)
      : Timestamp.now();
    const deadlineTimestamp = productInfo.deadline 
      ? Timestamp.fromDate(productInfo.deadline) 
      : Timestamp.fromDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)); // 30 days from now
    
    const userId = userRef.id;
    console.log('🔑 createDebitReceipt: Using userId:', userId);
    
    console.log('📄 createDebitReceipt: About to create receipt with data:', {
      type: 'debit',
      amount: productInfo.price,
      product: {
        productRef: productRef.path,
        name: productInfo.name,
        price: productInfo.price
      },
      organizationId,
      academyId
    });

    // Determine initial financial status based on deadline
    const now = new Date();
    const deadlineDate = deadlineTimestamp.toDate();
    const initialFinancialStatus = deadlineDate < now ? 'overdue' : 'pending';

    const productData: any = {
      productRef,
      name: productInfo.name,
      price: productInfo.price,
      invoiceDate,
      deadline: deadlineTimestamp
    };

    // Include discount info if present
    if (productInfo.originalPrice !== undefined) {
      productData.originalPrice = productInfo.originalPrice;
    }
    if (productInfo.discountApplied !== undefined) {
      productData.discountApplied = productInfo.discountApplied;
    }

    const receiptData: any = {
      type: 'debit',
      amount: productInfo.price,
      financialStatus: initialFinancialStatus, // Set initial financial status
      product: productData,
      siblingReceiptRefs: [],
      organizationId
    };
    
    // Only add academyId if it's defined
    if (academyId !== undefined) {
      receiptData.academyId = academyId;
    }
    
    console.log('💾 createDebitReceipt: Calling createReceipt with userId:', userId);
    console.log('💾 createDebitReceipt: Receipt data to create:', receiptData);
    
    const receipt = await createReceipt(userId, receiptData);

    console.log('✅ createDebitReceipt: Receipt created successfully:', receipt.id);
    console.log('✅ createDebitReceipt: Full receipt object:', receipt);

    // Update aggregated stats (non-blocking)
    try {
      await addDebitItem(organizationId, receipt);
      const statsDelta: Record<string, number> = {};
      statsDelta[initialFinancialStatus] = receipt.amount;
      await updateOrgStats(organizationId, academyId, statsDelta as any);
    } catch (statsErr) {
      console.error('Stats update error (non-blocking):', statsErr);
    }

    // Automatically apply available credits if any exist
    try {
      await autoApplyAvailableCredits(userId, organizationId, receipt);
      console.log('✅ createDebitReceipt: Automatic credit application completed');
    } catch (creditError) {
      console.error('❌ createDebitReceipt: Error applying available credits (non-blocking):', creditError);
      // Don't throw error to avoid failing receipt creation
    }

    // Update user's financial status based on the new receipt
    try {
      // If the new receipt is overdue or pending, user status needs to reflect that
      const userRef = doc(db, 'users', userId);
      const currentUserDoc = await getDoc(userRef);
      const currentStatus = currentUserDoc.data()?.financialStatus?.[organizationId];

      // Priority: overdue > pending > settled
      // Only update if new receipt's status is higher priority
      if (initialFinancialStatus === 'overdue' ||
          (initialFinancialStatus === 'pending' && currentStatus !== 'overdue')) {
        await updateDoc(userRef, {
          [`financialStatus.${organizationId}`]: initialFinancialStatus,
          updatedAt: serverTimestamp()
        });
        console.log(`📋 User ${userId} financial status updated to: ${initialFinancialStatus}`);
      }
    } catch (statusError) {
      console.error('❌ createDebitReceipt: Error updating user financial status (non-blocking):', statusError);
    }

    return receipt;
  } catch (error: any) {
    console.error('❌ createDebitReceipt: Error creating debit receipt:', error);
    console.error('❌ createDebitReceipt: Error message:', error?.message || error);
    console.error('❌ createDebitReceipt: Error stack:', error?.stack || error);
    console.error('❌ createDebitReceipt: Error details:', {
      userRefId: userRef?.id,
      productInfo,
      organizationId,
      academyId
    });
    throw error;
  }
};


export const getReceiptById = async (userId: string, receiptId: string): Promise<Receipt | null> => {
  try {
    const receiptRef = doc(db, 'users', userId, 'receipts', receiptId);
    const receiptSnap = await getDoc(receiptRef);
    
    if (receiptSnap.exists()) {
      return { id: receiptSnap.id, ...receiptSnap.data() } as Receipt;
    }
    return null;
  } catch (error) {
    console.error('Error getting receipt:', error);
    throw error;
  }
};

export const getReceiptsByUser = async (userId: string): Promise<Receipt[]> => {
  try {
    const userRef = doc(db, 'users', userId);
    const receiptsCollection = collection(userRef, 'receipts');
    const querySnapshot = await getDocs(receiptsCollection);
    
    const receipts = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Receipt[];
    
    return receipts.sort((a, b) => {
      const dateA = a.type === 'debit' && a.product ? a.product.invoiceDate?.toMillis() || 0 : (a.paymentDate?.toMillis() || a.createdAt?.toMillis() || 0);
      const dateB = b.type === 'debit' && b.product ? b.product.invoiceDate?.toMillis() || 0 : (b.paymentDate?.toMillis() || b.createdAt?.toMillis() || 0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error getting receipts by user:', error);
    throw error;
  }
};

export const getReceiptsByOrganization = async (organizationId: string): Promise<Receipt[]> => {
  try {
    // Note: Querying across subcollections requires composite queries or collection group queries
    // For now, we'll use collectionGroup which queries all 'receipts' subcollections
    const q = query(
      collectionGroup(db, 'receipts'),
      where('organizationId', '==', organizationId)
    );
    const querySnapshot = await getDocs(q);

    const receipts = querySnapshot.docs.map(docSnap => {
      const data = docSnap.data();
      const pathUserId = docSnap.ref.parent.parent?.id;
      return {
        id: docSnap.id,
        ...data,
        // Backfill userRef from Firestore path when the document doesn't have one.
        // Path: users/{userId}/receipts/{receiptId} → parent.parent = user doc ref.
        ...(!data.userRef && pathUserId ? { userRef: doc(db, 'users', pathUserId) } : {})
      };
    }) as Receipt[];
    
    return receipts.sort((a, b) => {
      const dateA = a.type === 'debit' && a.product ? a.product.invoiceDate?.toMillis() || 0 : (a.paymentDate?.toMillis() || a.createdAt?.toMillis() || 0);
      const dateB = b.type === 'debit' && b.product ? b.product.invoiceDate?.toMillis() || 0 : (b.paymentDate?.toMillis() || b.createdAt?.toMillis() || 0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error getting receipts by organization:', error);
    throw error;
  }
};

// Get unpaid debit receipts (no sibling credit receipts)
export const getUnpaidDebitReceipts = async (organizationId: string): Promise<Receipt[]> => {
  try {
    const q = query(
      collectionGroup(db, 'receipts'),
      where('organizationId', '==', organizationId),
      where('type', '==', 'debit')
    );
    const querySnapshot = await getDocs(q);
    
    const receipts = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Receipt[];
    
    // Filter to only unpaid debits (no sibling receipts)
    const unpaidReceipts = receipts.filter(receipt => 
      !receipt.siblingReceiptRefs || receipt.siblingReceiptRefs.length === 0
    );
    
    return unpaidReceipts.sort((a, b) => {
      const dateA = a.product?.invoiceDate?.toMillis() || a.createdAt?.toMillis() || 0;
      const dateB = b.product?.invoiceDate?.toMillis() || b.createdAt?.toMillis() || 0;
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error getting unpaid debit receipts:', error);
    throw error;
  }
};

// Get paid debit receipts (have sibling credit receipts)
export const getPaidDebitReceipts = async (organizationId: string): Promise<Receipt[]> => {
  try {
    const q = query(
      collectionGroup(db, 'receipts'),
      where('organizationId', '==', organizationId),
      where('type', '==', 'debit')
    );
    const querySnapshot = await getDocs(q);
    
    const receipts = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Receipt[];
    
    // Filter to only paid debits (have sibling receipts)
    const paidReceipts = receipts.filter(receipt => 
      receipt.siblingReceiptRefs && receipt.siblingReceiptRefs.length > 0
    );
    
    return paidReceipts.sort((a, b) => {
      const dateA = a.product?.invoiceDate?.toMillis() || a.createdAt?.toMillis() || 0;
      const dateB = b.product?.invoiceDate?.toMillis() || b.createdAt?.toMillis() || 0;
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error getting paid debit receipts:', error);
    throw error;
  }
};

export const getOverdueReceipts = async (organizationId: string): Promise<Receipt[]> => {
  try {
    const now = Timestamp.now();
    // Get all debit receipts for the organization
    const q = query(
      collectionGroup(db, 'receipts'),
      where('organizationId', '==', organizationId),
      where('type', '==', 'debit')
    );
    const querySnapshot = await getDocs(q);
    
    const receipts = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Receipt[];
    
    // Filter overdue receipts: unpaid debits (no sibling receipts) that are past deadline
    const overdueReceipts = receipts.filter(receipt => 
      receipt.type === 'debit' && 
      (!receipt.siblingReceiptRefs || receipt.siblingReceiptRefs.length === 0) &&
      receipt.product && 
      receipt.product.deadline?.toMillis() < now.toMillis()
    );
    
    return overdueReceipts.sort((a, b) => {
      const deadlineA = a.product?.deadline?.toMillis() || 0;
      const deadlineB = b.product?.deadline?.toMillis() || 0;
      return deadlineA - deadlineB;
    });
  } catch (error) {
    console.error('Error getting overdue receipts:', error);
    throw error;
  }
};

export const updateReceipt = async (userId: string, receiptId: string, updates: Partial<Receipt>): Promise<void> => {
  try {
    const receiptRef = doc(db, 'users', userId, 'receipts', receiptId);
    await updateDoc(receiptRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    // Get the receipt to find organizationId for balance update
    const updatedReceipt = await getReceiptById(userId, receiptId);
    if (updatedReceipt) {
      try {
        await recalculateAndUpdateUserBalance(userId, updatedReceipt.organizationId);
        await recalculateAndUpdateUserOutstandingAndCredits(userId, updatedReceipt.organizationId);
        console.log('💰 receiptService: User balance and outstanding/credits updated after receipt update');
      } catch (balanceError) {
        console.error('❌ receiptService: Error updating user balance after receipt update:', balanceError);
      }
    }
  } catch (error) {
    console.error('Error updating receipt:', error);
    throw error;
  }
};

// Note: Receipt status is now determined by sibling receipts, not a separate status field

export const deleteReceipt = async (userId: string, receiptId: string): Promise<void> => {
  try {
    // Get the receipt before deleting to find organizationId for balance update
    const receipt = await getReceiptById(userId, receiptId);
    const organizationId = receipt?.organizationId;
    
    const receiptRef = doc(db, 'users', userId, 'receipts', receiptId);
    await deleteDoc(receiptRef);
    
    // Update user balance after deleting receipt
    if (organizationId) {
      try {
        await recalculateAndUpdateUserBalance(userId, organizationId);
        await recalculateAndUpdateUserOutstandingAndCredits(userId, organizationId);
        console.log('💰 receiptService: User balance and outstanding/credits updated after receipt deletion');
      } catch (balanceError) {
        console.error('❌ receiptService: Error updating user balance after receipt deletion:', balanceError);
      }
    }
  } catch (error) {
    console.error('Error deleting receipt:', error);
    throw error;
  }
};

// Helper function to link sibling receipts and check if debit is fully paid
export const linkSiblingReceipts = async (
  debitUserId: string,
  debitReceiptId: string,
  creditUserId: string,
  creditReceiptId: string,
  isFullPayment: boolean = true, // Whether this payment fully covers the debit
  organizationId?: string // Optional: for updating user financial status
): Promise<void> => {
  try {
    console.log('🔗 linkSiblingReceipts: Linking and updating status...', {
      debitUserId,
      debitReceiptId,
      creditUserId,
      creditReceiptId
    });

    const batch = writeBatch(db);

    const debitRef = doc(db, 'users', debitUserId, 'receipts', debitReceiptId);
    const creditRef = doc(db, 'users', creditUserId, 'receipts', creditReceiptId);

    // Read debit BEFORE batch commit to capture current status for stats
    let debitDataBeforeUpdate: Receipt | null = null;
    if (organizationId) {
      try {
        const debitSnap = await getDoc(debitRef);
        if (debitSnap.exists()) {
          debitDataBeforeUpdate = debitSnap.data() as Receipt;
        }
      } catch (readErr) {
        console.error('Could not read debit before update (non-blocking):', readErr);
      }
    }

    // Update debit receipt - APPEND credit link using arrayUnion and update status
    const debitUpdate: any = {
      siblingReceiptRefs: arrayUnion(creditRef), // Use arrayUnion to append instead of overwrite
      updatedAt: serverTimestamp()
    };

    // Only mark as completed if this is a full payment
    if (isFullPayment) {
      debitUpdate.status = 'completed';
      debitUpdate.financialStatus = 'settled'; // Update financial status to settled
      console.log('📋 linkSiblingReceipts: Marking debit receipt as completed/settled (full payment)');
    } else {
      debitUpdate.status = 'paid'; // Partially paid but not fully completed
      // Financial status remains pending or overdue (not settled yet)
      console.log('📋 linkSiblingReceipts: Marking debit receipt as paid (partial payment)');
    }

    batch.update(debitRef, debitUpdate);

    // Update credit receipt - APPEND debit link using arrayUnion and mark as completed
    batch.update(creditRef, {
      siblingReceiptRefs: arrayUnion(debitRef), // Use arrayUnion to append instead of overwrite
      status: 'completed', // Credit is fully consumed when linked to a debit
      updatedAt: serverTimestamp()
    });

    await batch.commit();
    console.log('✅ linkSiblingReceipts: Successfully linked receipts and marked debit as', isFullPayment ? 'completed/settled' : 'paid');

    // Update aggregated stats only on the FIRST credit application (debit was 'active').
    // If debit was already 'paid' (partial payments applied before), the counter was already decremented — skip to avoid double-decrement.
    if (organizationId && debitDataBeforeUpdate &&
        debitDataBeforeUpdate.status === 'active' &&
        (debitDataBeforeUpdate.financialStatus === 'pending' || debitDataBeforeUpdate.financialStatus === 'overdue')) {
      try {
        const field = debitDataBeforeUpdate.financialStatus; // 'pending' or 'overdue'
        await updateOrgStats(organizationId, debitDataBeforeUpdate.academyId, {
          [field]: -debitDataBeforeUpdate.amount
        } as any);
        console.log(`📊 linkSiblingReceipts: Decremented ${field} by ${debitDataBeforeUpdate.amount}`);
      } catch (statsErr) {
        console.error('Stats update error in linkSiblingReceipts (non-blocking):', statsErr);
      }
    }

    // Update user's financial status if full payment and organizationId provided
    if (isFullPayment && organizationId) {
      try {
        // Need to re-check user's status by querying other receipts
        const userRef = doc(db, 'users', debitUserId);
        const receiptsCollection = collection(userRef, 'receipts');

        // Check if user has any OTHER overdue receipts
        const overdueQuery = query(
          receiptsCollection,
          where('organizationId', '==', organizationId),
          where('type', '==', 'debit'),
          where('financialStatus', '==', 'overdue')
        );
        const overdueSnapshot = await getDocs(overdueQuery);
        const hasOtherOverdue = overdueSnapshot.docs.some(d => d.id !== debitReceiptId);

        if (hasOtherOverdue) {
          // User still has overdue receipts
          console.log('📋 User still has other overdue receipts, status remains overdue');
          return;
        }

        // Check if user has any OTHER pending receipts
        const pendingQuery = query(
          receiptsCollection,
          where('organizationId', '==', organizationId),
          where('type', '==', 'debit'),
          where('financialStatus', '==', 'pending')
        );
        const pendingSnapshot = await getDocs(pendingQuery);
        const hasOtherPending = pendingSnapshot.docs.some(d => d.id !== debitReceiptId);

        if (hasOtherPending) {
          await updateDoc(userRef, {
            [`financialStatus.${organizationId}`]: 'pending',
            updatedAt: serverTimestamp()
          });
          console.log('📋 User financial status updated to: pending');
        } else {
          await updateDoc(userRef, {
            [`financialStatus.${organizationId}`]: 'settled',
            updatedAt: serverTimestamp()
          });
          console.log('📋 User financial status updated to: settled');
        }
      } catch (statusError) {
        console.error('❌ linkSiblingReceipts: Error updating user financial status:', statusError);
      }
    }
  } catch (error) {
    console.error('Error linking sibling receipts:', error);
    throw error;
  }
};

// Helper function to get user balance from receipts
// Negative balance = user owes money, Positive balance = user has credit
export const calculateUserBalanceFromReceipts = async (userId: string, organizationId?: string): Promise<number> => {
  try {
    const receipts = await getReceiptsByUser(userId);

    // Filter by organization if provided and exclude deleted receipts
    const filteredReceipts = receipts.filter(r => {
      if (r.status === 'deleted') return false;
      if (organizationId && r.organizationId !== organizationId) return false;
      return true;
    });

    const balance = filteredReceipts.reduce((acc, receipt) => {
      if (receipt.type === 'debit') {
        // Debit receipts represent money owed (negative impact on balance)
        return acc - receipt.amount;
      } else if (receipt.type === 'credit') {
        // Credit receipts represent payments made (positive impact on balance)
        return acc + receipt.amount;
      }
      return acc;
    }, 0);

    return balance;
  } catch (error) {
    console.error('Error calculating user balance from receipts:', error);
    throw error;
  }
};


// Backward compatibility
export const getUserBalance = calculateUserBalanceFromReceipts;

// Helper function to get pending debit receipts for a user
export const getPendingDebitReceipts = async (
  userId: string,
  organizationId: string
): Promise<Receipt[]> => {
  try {
    const userReceipts = await getReceiptsByUser(userId);
    // Filter by organization and exclude deleted receipts
    const organizationReceipts = userReceipts.filter(r =>
      r.organizationId === organizationId && r.status !== 'deleted'
    );
    const debitReceipts = organizationReceipts.filter(r => r.type === 'debit');
    const pendingDebits: Receipt[] = [];

    for (const debitReceipt of debitReceipts) {
      // Don't skip completed debits — verify actual credit coverage instead.
      // A debit may still be marked 'completed' after its payment was deleted.

      // Calculate total credits applied to this debit (exclude deleted credits)
      const linkedCredits = organizationReceipts.filter(creditReceipt =>
        creditReceipt.type === 'credit' &&
        creditReceipt.status !== 'deleted' &&
        creditReceipt.siblingReceiptRefs &&
        creditReceipt.siblingReceiptRefs.some(ref => ref.id === debitReceipt.id)
      );

      const totalCreditsApplied = linkedCredits.reduce((sum, credit) => sum + credit.amount, 0);
      const remainingDebt = debitReceipt.amount - totalCreditsApplied;

      // Only include debits with remaining balance (active or partially paid)
      if (remainingDebt > 0) {
        pendingDebits.push(debitReceipt);
      }
    }

    return pendingDebits.sort((a, b) => {
      const dateA = a.product?.invoiceDate?.toMillis() || a.createdAt?.toMillis() || 0;
      const dateB = b.product?.invoiceDate?.toMillis() || b.createdAt?.toMillis() || 0;
      return dateA - dateB;
    });
  } catch (error) {
    console.error('Error getting pending debit receipts:', error);
    throw error;
  }
};

// Create a credit receipt for payments
export const createCreditReceipt = async (
  userRef: DocumentReference,
  amount: number,
  parentTransactionRef?: DocumentReference,
  siblingReceiptRef?: DocumentReference,
  organizationId?: string,
  academyId?: string,
  description?: string,
  transactionDate?: Date,
  paymentMethod?: string
): Promise<Receipt> => {
  try {
    console.log('💳 createCreditReceipt: Creating credit receipt for:', {
      userRefId: userRef.id,
      amount,
      organizationId,
      academyId,
      description
    });

    const userId = userRef.id;
    
    const creditReceiptData: any = {
      type: 'credit' as const,
      amount: amount,
      description: description || `Credit for payment`,
      organizationId: organizationId || '',
      invoiceDate: transactionDate ? Timestamp.fromDate(transactionDate) : Timestamp.now(),
      paidDate: transactionDate ? Timestamp.fromDate(transactionDate) : Timestamp.now(),
      siblingReceiptRefs: siblingReceiptRef ? [siblingReceiptRef] : []
    };

    // Only add parentTransactionRef if it's defined
    if (parentTransactionRef) {
      creditReceiptData.parentTransactionRef = parentTransactionRef;
    }

    // Only add academyId if it's defined
    if (academyId !== undefined) {
      creditReceiptData.academyId = academyId;
    }

    const creditReceipt = await createReceipt(userId, creditReceiptData);

    console.log('✅ createCreditReceipt: Credit receipt created:', creditReceipt.id);

    // Update aggregated stats (non-blocking)
    try {
      await addCreditItem(organizationId || '', creditReceipt, paymentMethod || 'Cash');
      // If no sibling debit receipt, this is unlinked credit → add to availableCredit
      if (!siblingReceiptRef) {
        await updateOrgStats(organizationId || '', academyId, { availableCredit: amount });
      }
    } catch (statsErr) {
      console.error('Stats update error (non-blocking):', statsErr);
    }

    // If there's a sibling debit receipt, link them and update debit status
    if (siblingReceiptRef) {
      console.log('🔗 Linking credit receipt to debit receipt and updating status...');
      
      // Check if this is a full payment by calculating the remaining debt
      try {
        const debitReceiptDoc = await getDoc(siblingReceiptRef);
        const debitReceiptData = debitReceiptDoc.data() as Receipt;
        
        // Get all existing credit receipts linked to this debit to calculate remaining debt
        const userReceipts = await getReceiptsByUser(userId);
        const existingCreditsForDebit = userReceipts.filter(r => 
          r.type === 'credit' &&
          r.siblingReceiptRefs &&
          r.siblingReceiptRefs.some(ref => ref.id === siblingReceiptRef.id) &&
          r.id !== creditReceipt.id // Don't count the current credit receipt
        );
        
        const totalExistingCredits = existingCreditsForDebit.reduce((sum, r) => sum + r.amount, 0);
        const remainingDebt = debitReceiptData.amount - totalExistingCredits;
        const isFullPayment = amount >= remainingDebt;
        
        console.log('💰 Payment comparison:', {
          debitAmount: debitReceiptData.amount,
          existingCredits: totalExistingCredits,
          remainingDebt: remainingDebt,
          creditAmount: amount,
          isFullPayment
        });
        
        await linkSiblingReceipts(
          userId, // debitUserId
          siblingReceiptRef.id, // debitReceiptId
          userId, // creditUserId (same user)
          creditReceipt.id, // creditReceiptId
          isFullPayment, // Pass whether this is a full payment
          organizationId // Pass organizationId to update user financial status
        );

        console.log(`✅ Debit receipt marked as ${isFullPayment ? 'completed' : 'paid'} and linked to credit receipt`);
      } catch (error) {
        console.error('Error checking debit receipt amount:', error);
        // Fallback to not changing status if we can't check
        await linkSiblingReceipts(
          userId,
          siblingReceiptRef.id,
          userId,
          creditReceipt.id,
          false, // Default to partial payment if we can't determine
          organizationId // Pass organizationId to update user financial status
        );
      }
    }
    
    // If no sibling debit was specified, auto-apply credit to outstanding debits (prioritize overdue)
    if (!siblingReceiptRef && organizationId) {
      try {
        console.log('🔄 createCreditReceipt: No sibling specified, auto-applying to outstanding debits...');
        await autoApplyCreditsToOutstandingDebits(userId, organizationId, creditReceipt);
      } catch (autoApplyError) {
        console.error('❌ createCreditReceipt: Error auto-applying credits (non-blocking):', autoApplyError);
        // Don't throw - credit was created successfully
      }
    }

    // Update user balance after creating the credit receipt
    await recalculateAndUpdateUserBalance(userId, organizationId);
    await recalculateAndUpdateUserOutstandingAndCredits(userId, organizationId || '');

    return creditReceipt;
  } catch (error) {
    console.error('❌ Error creating credit receipt:', error);
    throw error;
  }
};

// Calculate user's outstanding balance (pending debits minus available credits)
// Type for pending debit receipts with remaining amount
export type PendingDebitReceipt = Receipt & { remainingAmount: number };                      
             
export const calculateUserOutstandingBalance = async (
  userId: string,
  organizationId: string
): Promise<{
  outstandingDebits: number;
  availableCredits: number;
  netBalance: number;
  pendingDebitReceipts: PendingDebitReceipt[];
  creditReceipts: Receipt[];
}> => {
  try {
    const userReceipts = await getReceiptsByUser(userId);
    // Filter out deleted receipts and get organization-specific receipts
    // Include active, paid, and completed receipts in calculations
    const organizationReceipts = userReceipts.filter(r => 
      r.organizationId === organizationId && r.status !== 'deleted'
    );
    
    // Calculate outstanding debits by checking actual payment coverage
    const debitReceipts = organizationReceipts.filter(r => r.type === 'debit');
    const pendingDebitReceipts: PendingDebitReceipt[] = [];
    let outstandingDebits = 0;

    for (const debitReceipt of debitReceipts) {
      // Don't skip completed debits — verify actual credit coverage instead.
      // A debit may still be marked 'completed' after its payment was deleted.

      // Calculate total credits applied to this debit (exclude deleted credits)
      const linkedCredits = organizationReceipts.filter(creditReceipt =>
        creditReceipt.type === 'credit' &&
        creditReceipt.status !== 'deleted' &&
        creditReceipt.siblingReceiptRefs &&
        creditReceipt.siblingReceiptRefs.some(ref => ref.id === debitReceipt.id)
      );
      
      const totalCreditsApplied = linkedCredits.reduce((sum, credit) => sum + credit.amount, 0);
      const remainingDebt = debitReceipt.amount - totalCreditsApplied;
      
      // Only include in outstanding if there's remaining debt
      // This handles both 'active' (unpaid) and 'paid' (partially paid) statuses
      if (remainingDebt > 0) {
        // Add the debit receipt with its remaining amount
        const pendingDebit: PendingDebitReceipt = {
          ...debitReceipt,
          remainingAmount: remainingDebt
        };
        pendingDebitReceipts.push(pendingDebit);
        outstandingDebits += remainingDebt;
      }
    }
    
    // All credit receipts (payments made)
    const creditReceipts = organizationReceipts.filter(r => 
      r.type === 'credit'
    );
    
    // Only count unlinked credit receipts as available credits
    // (Credits linked to debits are already "used" to pay those debts)
    // Also count credits whose ALL siblings are deleted — those debits no longer exist
    const availableCredits = creditReceipts
      .filter(r => {
        if (!r.siblingReceiptRefs || r.siblingReceiptRefs.length === 0) return true;
        // If every sibling ref points to a deleted (or missing) receipt, credit is free
        return r.siblingReceiptRefs.every(ref => {
          const sibling = userReceipts.find(d => d.id === ref.id);
          return !sibling || sibling.status === 'deleted';
        });
      })
      .reduce((sum, r) => sum + r.amount, 0);
    
    // netBalance represents the true net financial position
    // Positive = owes money, Negative = has credit balance, Zero = balanced
    const netBalance = outstandingDebits - availableCredits;
    
    
    return {
      outstandingDebits, // Raw pending debit amount
      availableCredits,  // Unlinked credit amount
      netBalance,        // What they actually owe (outstandingDebits - availableCredits, never negative)
      pendingDebitReceipts,
      creditReceipts
    };
  } catch (error) {
    console.error('Error calculating user outstanding balance:', error);
    throw error;
  }
};

// Helper function to get user's receipt summary
export const getUserReceiptSummary = async (
  userId: string,
  organizationId: string
): Promise<{
  totalDebit: number;
  totalCredit: number;
  pendingDebit: number;
  paidDebit: number;
  balance: number;
}> => {
  try {
    const receipts = await getReceiptsByUser(userId);
    const orgReceipts = receipts.filter(r => r.organizationId === organizationId);
    
    const summary = {
      totalDebit: 0,
      totalCredit: 0,
      pendingDebit: 0,
      paidDebit: 0,
      balance: 0
    };
    
    orgReceipts.forEach(receipt => {
      if (receipt.type === 'debit') {
        summary.totalDebit += receipt.amount;
        // Check if debit has sibling credit receipts (paid) or not (pending)
        if (!receipt.siblingReceiptRefs || receipt.siblingReceiptRefs.length === 0) {
          summary.pendingDebit += receipt.amount;
        } else {
          summary.paidDebit += receipt.amount;
        }
      } else if (receipt.type === 'credit') {
        summary.totalCredit += receipt.amount;
      }
    });
    
    summary.balance = summary.totalCredit - summary.totalDebit;
    
    return summary;
  } catch (error) {
    console.error('Error getting user receipt summary:', error);
    throw error;
  }
};

// Automatically apply available credits to new debit receipt via internal transaction
export const autoApplyAvailableCredits = async (
  userId: string,
  organizationId: string,
  newDebitReceipt: Receipt
): Promise<void> => {
  try {
    console.log(`🔄 autoApplyAvailableCredits: Checking for available credits for user ${userId}`);
    
    // Get user's current balance info
    const balanceInfo = await calculateUserOutstandingBalance(userId, organizationId);
    
    if (balanceInfo.availableCredits <= 0) {
      console.log(`💡 autoApplyAvailableCredits: No available credits (${balanceInfo.availableCredits}) - skipping`);
      return;
    }
    
    console.log(`💰 autoApplyAvailableCredits: Found ${balanceInfo.availableCredits} available credits`);
    
    // Calculate how much credit to apply (minimum of available credit and debit amount)
    const creditToApply = Math.min(balanceInfo.availableCredits, newDebitReceipt.amount);
    
    if (creditToApply <= 0) {
      console.log(`💡 autoApplyAvailableCredits: No credit to apply - skipping`);
      return;
    }
    
    console.log(`✨ autoApplyAvailableCredits: Applying ${creditToApply} credit to debit ${newDebitReceipt.id}`);
    
    // Create internal transaction to apply available credits
    const { createTransaction } = await import('./transactionService');
    
    const internalTransactionData: any = {
      amount: creditToApply, // Show the actual credit amount being applied
      paymentMethod: 'Credit Application',
      transactionOwner: {
        name: 'System',
        userRef: newDebitReceipt.userRef
      },
      description: `Automatic credit application of ${creditToApply} for ${newDebitReceipt.product?.name || 'charge'}`,
      date: Timestamp.now(),
      type: 'internal',
      handler: {
        name: 'System',
        userRef: newDebitReceipt.userRef
      },
      status: 'completed',
      organizationId
    };
    
    // Only add academyId if it's defined
    if (newDebitReceipt.academyId !== undefined) {
      internalTransactionData.academyId = newDebitReceipt.academyId;
    }
    
    // Create the internal transaction
    const internalTransaction = await createTransaction(internalTransactionData);
    console.log(`📝 autoApplyAvailableCredits: Created internal transaction ${internalTransaction.id}`);
    
    // Get unlinked credit receipts to consume (oldest first)
    const unlinkedCredits = balanceInfo.creditReceipts.filter(r => 
      !r.siblingReceiptRefs || r.siblingReceiptRefs.length === 0
    ).sort((a, b) => {
      // Use oldest credits first (FIFO)
      const dateA = a.paymentDate?.toMillis() || a.createdAt?.toMillis() || 0;
      const dateB = b.paymentDate?.toMillis() || b.createdAt?.toMillis() || 0;
      return dateA - dateB;
    });
    
    const siblingDebitReceiptRef = doc(db, 'users', userId, 'receipts', newDebitReceipt.id);
    let remainingCreditToApply = creditToApply;
    let linkSiblingHandledStats = false; // Track if linkSiblingReceipts already adjusted pending/overdue

    // Consume available credits by linking them to the new debit receipt
    for (const creditReceipt of unlinkedCredits) {
      if (remainingCreditToApply <= 0) break;

      const creditAmountToUse = Math.min(remainingCreditToApply, creditReceipt.amount);

      if (creditAmountToUse === creditReceipt.amount) {
        // Use the entire credit receipt - just link it to the debit
        await updateReceipt(userId, creditReceipt.id, {
          siblingReceiptRefs: [siblingDebitReceiptRef]
        });
        console.log(`🔗 autoApplyAvailableCredits: Linked entire credit receipt ${creditReceipt.id} (${creditAmountToUse}) to debit`);
      } else {
        // Partial use - need to split the credit receipt
        const remainingCredit = creditReceipt.amount - creditAmountToUse;
        
        if (remainingCredit > 0) {
          // Update the original to reduce its amount and update description
          await updateReceipt(userId, creditReceipt.id, { 
            amount: remainingCredit,
            description: `${creditReceipt.description || 'Available credit'} (reduced from ${creditReceipt.amount} after ${creditAmountToUse} was applied)`
          });
          console.log(`✂️ autoApplyAvailableCredits: Reduced credit receipt amount to ${remainingCredit}`);
        } else {
          // Mark the original receipt as fully consumed by linking it to the debit
          await updateReceipt(userId, creditReceipt.id, { 
            amount: 0,
            siblingReceiptRefs: [siblingDebitReceiptRef],
            description: `${creditReceipt.description || 'Available credit'} (fully consumed - applied ${creditAmountToUse} to invoice)`,
            status: 'completed'
          });
          console.log(`🔗 autoApplyAvailableCredits: Marked credit receipt ${creditReceipt.id} as fully consumed`);
        }
        
        // Create a new linked credit receipt for the used portion
        // This calls linkSiblingReceipts internally, which handles pending/overdue stats
        await createCreditReceipt(
          newDebitReceipt.userRef,
          creditAmountToUse,
          doc(db, 'transactions', internalTransaction.id),
          siblingDebitReceiptRef,
          organizationId,
          newDebitReceipt.academyId,
          `Automatic credit application from available balance (partial: ${creditAmountToUse})`
        );
        linkSiblingHandledStats = true;

        console.log(`✂️ autoApplyAvailableCredits: Split credit receipt - used ${creditAmountToUse}, remaining ${remainingCredit}`);
      }
      
      remainingCreditToApply -= creditAmountToUse;
    }
    
    console.log(`💳 autoApplyAvailableCredits: Applied ${creditToApply} credit to debit ${newDebitReceipt.id}`);
    
    // Update the debit receipt to reflect the credit application
    const isFullyPaid = creditToApply >= newDebitReceipt.amount;
    const newStatus = isFullyPaid ? 'completed' : 'paid';
    
    // Get all credit receipts that are now linked to this debit
    const linkedCreditRefs = unlinkedCredits
      .filter((_, index) => {
        // Calculate how much credit was applied from each receipt
        let appliedSoFar = 0;
        for (let i = 0; i <= index; i++) {
          appliedSoFar += Math.min(unlinkedCredits[i].amount, creditToApply - appliedSoFar);
          if (appliedSoFar >= creditToApply) break;
        }
        return appliedSoFar <= creditToApply;
      })
      .map(credit => doc(db, 'users', userId, 'receipts', credit.id));
    
    // Update the debit receipt with links back to credit receipts and new status
    await updateReceipt(userId, newDebitReceipt.id, {
      siblingReceiptRefs: linkedCreditRefs,
      status: newStatus
    });
    
    console.log(`📋 autoApplyAvailableCredits: Updated debit receipt status to '${newStatus}' and linked ${linkedCreditRefs.length} credit receipts`);
    
    // Immediately update the user's stored balance fields to reflect the credit application
    console.log(`🔄 autoApplyAvailableCredits: Updating user balance fields after credit application`);
    await recalculateAndUpdateUserOutstandingAndCredits(userId, organizationId);
    console.log(`✅ autoApplyAvailableCredits: User balance fields updated`);
    
    // Update aggregated stats (non-blocking)
    try {
      // Decrease availableCredit — these unlinked credits are now consumed
      await updateOrgStats(organizationId, newDebitReceipt.academyId, { availableCredit: -creditToApply });
      console.log(`📊 autoApplyAvailableCredits: Decremented availableCredit by ${creditToApply}`);

      // Decrease pending/overdue — debit went from 'active' to 'paid'/'completed'
      // Only if linkSiblingReceipts didn't already handle it (Path B / split credits)
      // Use the explicit financialStatus field set during createDebitReceipt
      if (!linkSiblingHandledStats && newDebitReceipt.financialStatus) {
        const field = newDebitReceipt.financialStatus; // 'pending' or 'overdue'
        if (field === 'pending' || field === 'overdue') {
          await updateOrgStats(organizationId, newDebitReceipt.academyId, {
            [field]: -newDebitReceipt.amount
          } as any);
          console.log(`📊 autoApplyAvailableCredits: Decremented ${field} by ${newDebitReceipt.amount}`);
        }
      }
    } catch (statsErr) {
      console.error('Stats update error in autoApplyAvailableCredits (non-blocking):', statsErr);
    }

    console.log(`✅ autoApplyAvailableCredits: Successfully applied ${creditToApply} credit to new debit receipt`);
    
  } catch (error) {
    console.error('❌ autoApplyAvailableCredits: Error applying available credits:', error);
    // Don't throw error to avoid failing the product addition
  }
};

// Auto-apply a new credit receipt to outstanding debit receipts (prioritize overdue)
export const autoApplyCreditsToOutstandingDebits = async (
  userId: string,
  organizationId: string,
  newCreditReceipt: Receipt
): Promise<void> => {
  try {
    console.log(`🔄 autoApplyCreditsToOutstandingDebits: Applying credit ${newCreditReceipt.id} to outstanding debits`);

    // Get all user receipts
    const userReceipts = await getReceiptsByUser(userId);

    // Find outstanding debit receipts (active status, not fully paid)
    const outstandingDebits = userReceipts
      .filter(r =>
        r.type === 'debit' &&
        r.organizationId === organizationId &&
        (r.status === 'active' || r.status === 'paid') // Include partially paid
      )
      .map(debit => {
        // Calculate remaining amount for this debit (exclude deleted credits)
        const linkedCredits = userReceipts.filter(r =>
          r.type === 'credit' &&
          r.status !== 'deleted' &&
          r.siblingReceiptRefs?.some(ref => ref.id === debit.id)
        );
        const totalPaid = linkedCredits.reduce((sum, c) => sum + c.amount, 0);
        const remainingAmount = debit.amount - totalPaid;
        return { ...debit, remainingAmount };
      })
      .filter(d => d.remainingAmount > 0) // Only debits with outstanding balance
      .sort((a, b) => {
        // Prioritize overdue receipts (past deadline)
        const now = new Date();
        const aDeadline = a.product?.deadline?.toDate?.() || new Date(0);
        const bDeadline = b.product?.deadline?.toDate?.() || new Date(0);
        const aOverdue = aDeadline < now ? 1 : 0;
        const bOverdue = bDeadline < now ? 1 : 0;

        if (aOverdue !== bOverdue) return bOverdue - aOverdue; // Overdue first
        return aDeadline.getTime() - bDeadline.getTime(); // Then by oldest deadline
      });

    if (outstandingDebits.length === 0) {
      console.log('💡 autoApplyCreditsToOutstandingDebits: No outstanding debits to apply credit to');
      return;
    }

    console.log(`📋 autoApplyCreditsToOutstandingDebits: Found ${outstandingDebits.length} outstanding debits`);

    let remainingCredit = newCreditReceipt.amount;
    const linkedDebitRefs: DocumentReference[] = [];
    // Track stats adjustments for debits transitioning from 'active' to 'paid'/'completed'
    let overdueDecrement = 0;
    let pendingDecrement = 0;
    let totalCreditLinked = 0;

    for (const debit of outstandingDebits) {
      if (remainingCredit <= 0) break;

      const amountToApply = Math.min(remainingCredit, debit.remainingAmount);
      console.log(`💳 Applying ${amountToApply} to debit ${debit.id} (remaining: ${debit.remainingAmount})`);

      // Check if this fully pays the debit
      const isFullyPaid = amountToApply >= debit.remainingAmount;
      const newStatus = isFullyPaid ? 'completed' : 'paid';

      // Only decrement stats on the FIRST credit application (when transitioning from 'active').
      // If status is already 'paid', a previous credit already decremented the counter — skip to avoid double-decrement.
      if (debit.status === 'active') {
        if (debit.financialStatus === 'overdue') {
          overdueDecrement += debit.amount;
        } else if (debit.financialStatus === 'pending') {
          pendingDecrement += debit.amount;
        }
      }

      // Update the debit receipt
      const debitRef = doc(db, 'users', userId, 'receipts', debit.id);
      const existingSiblings = debit.siblingReceiptRefs || [];
      const creditRef = doc(db, 'users', userId, 'receipts', newCreditReceipt.id);

      await updateReceipt(userId, debit.id, {
        siblingReceiptRefs: [...existingSiblings, creditRef],
        status: newStatus,
        financialStatus: isFullyPaid ? 'settled' : undefined
      });

      linkedDebitRefs.push(debitRef);
      totalCreditLinked += amountToApply;
      remainingCredit -= amountToApply;

      console.log(`✅ Debit ${debit.id} updated to status '${newStatus}'`);
    }

    // Update the credit receipt to link to all the debits it was applied to
    // Also set credit status: 'completed' if fully consumed, 'paid' if partially used
    if (linkedDebitRefs.length > 0) {
      const existingCreditSiblings = newCreditReceipt.siblingReceiptRefs || [];
      const creditStatus = remainingCredit <= 0 ? 'completed' : 'paid';
      await updateReceipt(userId, newCreditReceipt.id, {
        siblingReceiptRefs: [...existingCreditSiblings, ...linkedDebitRefs],
        status: creditStatus
      });
      console.log(`🔗 Credit receipt linked to ${linkedDebitRefs.length} debit receipts, status set to '${creditStatus}'`);
    }

    // Update aggregated stats (non-blocking)
    // The credit was added to availableCredit by createCreditReceipt; now consume what was linked
    try {
      const academyId = newCreditReceipt.academyId;
      if (totalCreditLinked > 0) {
        await updateOrgStats(organizationId, academyId, { availableCredit: -totalCreditLinked });
        console.log(`📊 autoApplyCreditsToOutstandingDebits: Decremented availableCredit by ${totalCreditLinked}`);
      }
      if (overdueDecrement > 0) {
        await updateOrgStats(organizationId, academyId, { overdue: -overdueDecrement } as any);
        console.log(`📊 autoApplyCreditsToOutstandingDebits: Decremented overdue by ${overdueDecrement}`);
      }
      if (pendingDecrement > 0) {
        await updateOrgStats(organizationId, academyId, { pending: -pendingDecrement } as any);
        console.log(`📊 autoApplyCreditsToOutstandingDebits: Decremented pending by ${pendingDecrement}`);
      }
    } catch (statsErr) {
      console.error('Stats update error in autoApplyCreditsToOutstandingDebits (non-blocking):', statsErr);
    }

    // Update user's financial status
    await recalculateAndUpdateUserOutstandingAndCredits(userId, organizationId);

    console.log(`✅ autoApplyCreditsToOutstandingDebits: Applied credit to ${linkedDebitRefs.length} debits, remaining credit: ${remainingCredit}`);

  } catch (error) {
    console.error('❌ autoApplyCreditsToOutstandingDebits: Error:', error);
    throw error;
  }
};

// Note: Overdue status is now determined by checking deadline vs current date and sibling receipts
// No need for separate status updates - overdue receipts are simply unpaid debits past their deadline

// ============================================================================
// UTILITY: Fix existing unlinked credits by applying them to outstanding debits
// Run this once to clean up existing data
// ============================================================================
export const fixUnlinkedCreditsForOrganization = async (
  organizationId: string,
  onProgress?: (message: string) => void
): Promise<{
  usersProcessed: number;
  creditsApplied: number;
  debitsSettled: number;
  errors: string[];
}> => {
  const log = (msg: string) => {
    console.log(msg);
    onProgress?.(msg);
  };

  const result = {
    usersProcessed: 0,
    creditsApplied: 0,
    debitsSettled: 0,
    errors: [] as string[]
  };

  try {
    log(`🔧 Starting fix for organization: ${organizationId}`);

    // Get all receipts for the organization using collectionGroup
    const receiptsQuery = query(
      collectionGroup(db, 'receipts'),
      where('organizationId', '==', organizationId)
    );
    const receiptsSnapshot = await getDocs(receiptsQuery);

    log(`📋 Found ${receiptsSnapshot.size} total receipts`);

    // Group receipts by user
    const receiptsByUser = new Map<string, Receipt[]>();

    receiptsSnapshot.docs.forEach(docSnap => {
      const receipt = { id: docSnap.id, ...docSnap.data() } as Receipt;
      // Extract userId from the document path: users/{userId}/receipts/{receiptId}
      const pathParts = docSnap.ref.path.split('/');
      const userId = pathParts[1]; // users/{userId}/receipts/...

      if (!receiptsByUser.has(userId)) {
        receiptsByUser.set(userId, []);
      }
      receiptsByUser.get(userId)!.push(receipt);
    });

    log(`👥 Found ${receiptsByUser.size} users with receipts`);

    // Process each user
    const userEntries = Array.from(receiptsByUser.entries());
    for (const [userId, userReceipts] of userEntries) {
      try {
        // Find unlinked credit receipts (no siblingReceiptRefs or empty array)
        const unlinkedCredits = userReceipts.filter((r: Receipt) =>
          r.type === 'credit' &&
          r.status !== 'completed' &&
          (!r.siblingReceiptRefs || r.siblingReceiptRefs.length === 0)
        );

        if (unlinkedCredits.length === 0) {
          continue; // No unlinked credits for this user
        }

        log(`💳 User ${userId}: Found ${unlinkedCredits.length} unlinked credits`);

        // Find outstanding debit receipts
        type DebitWithRemaining = Receipt & { remainingAmount: number };
        const outstandingDebits: DebitWithRemaining[] = userReceipts
          .filter((r: Receipt) =>
            r.type === 'debit' &&
            (r.status === 'active' || r.status === 'paid')
          )
          .map((debit: Receipt) => {
            // Calculate remaining amount (exclude deleted credits)
            const linkedCredits = userReceipts.filter((r: Receipt) =>
              r.type === 'credit' &&
              r.status !== 'deleted' &&
              r.siblingReceiptRefs?.some((ref: DocumentReference) => ref.id === debit.id)
            );
            const totalPaid = linkedCredits.reduce((sum: number, c: Receipt) => sum + c.amount, 0);
            const remainingAmount = debit.amount - totalPaid;
            return { ...debit, remainingAmount };
          })
          .filter((d: DebitWithRemaining) => d.remainingAmount > 0)
          .sort((a: DebitWithRemaining, b: DebitWithRemaining) => {
            // Prioritize overdue (past deadline)
            const now = new Date();
            const aDeadline = a.product?.deadline?.toDate?.() || new Date(0);
            const bDeadline = b.product?.deadline?.toDate?.() || new Date(0);
            const aOverdue = aDeadline < now ? 1 : 0;
            const bOverdue = bDeadline < now ? 1 : 0;
            if (aOverdue !== bOverdue) return bOverdue - aOverdue;
            return aDeadline.getTime() - bDeadline.getTime();
          });

        if (outstandingDebits.length === 0) {
          log(`  ℹ️ User ${userId}: No outstanding debits to apply credits to`);
          continue;
        }

        log(`  📄 User ${userId}: Found ${outstandingDebits.length} outstanding debits`);

        // Apply each unlinked credit to outstanding debits
        for (const credit of unlinkedCredits) {
          let remainingCredit = credit.amount;
          const linkedDebitRefs: DocumentReference[] = [];

          for (const debit of outstandingDebits) {
            if (remainingCredit <= 0) break;
            if (debit.remainingAmount <= 0) continue;

            const amountToApply = Math.min(remainingCredit, debit.remainingAmount);
            const isFullyPaid = amountToApply >= debit.remainingAmount;
            const newStatus = isFullyPaid ? 'completed' : 'paid';

            // Update debit receipt
            const debitRef = doc(db, 'users', userId, 'receipts', debit.id);
            const existingSiblings = debit.siblingReceiptRefs || [];
            const creditRef = doc(db, 'users', userId, 'receipts', credit.id);

            await updateReceipt(userId, debit.id, {
              siblingReceiptRefs: [...existingSiblings, creditRef],
              status: newStatus,
              financialStatus: isFullyPaid ? 'settled' : undefined
            });

            linkedDebitRefs.push(debitRef);
            debit.remainingAmount -= amountToApply;
            remainingCredit -= amountToApply;

            if (isFullyPaid) {
              result.debitsSettled++;
            }

            log(`    ✅ Applied ${amountToApply} from credit ${credit.id} to debit ${debit.id} (${newStatus})`);
          }

          // Update the credit receipt to link to debits and set status
          if (linkedDebitRefs.length > 0) {
            const existingCreditSiblings = credit.siblingReceiptRefs || [];
            const creditStatus = remainingCredit <= 0 ? 'completed' : 'paid';
            await updateReceipt(userId, credit.id, {
              siblingReceiptRefs: [...existingCreditSiblings, ...linkedDebitRefs],
              status: creditStatus
            });
            result.creditsApplied++;
          }
        }

        // Recalculate user's financial status
        await recalculateAndUpdateUserOutstandingAndCredits(userId, organizationId);
        result.usersProcessed++;

        log(`  ✅ User ${userId}: Processing complete`);

      } catch (userError: any) {
        const errorMsg = `Error processing user ${userId}: ${userError.message}`;
        log(`  ❌ ${errorMsg}`);
        result.errors.push(errorMsg);
      }
    }

    log(`\n🎉 Fix complete!`);
    log(`   Users processed: ${result.usersProcessed}`);
    log(`   Credits applied: ${result.creditsApplied}`);
    log(`   Debits settled: ${result.debitsSettled}`);
    log(`   Errors: ${result.errors.length}`);

    return result;

  } catch (error: any) {
    console.error('❌ fixUnlinkedCreditsForOrganization: Fatal error:', error);
    result.errors.push(`Fatal error: ${error.message}`);
    return result;
  }
};

// Helper function to create receipts for player with product assignment
export const createPlayerProductReceipts = async (
  playerId: string,
  playerRef: DocumentReference,
  productRef: DocumentReference,
  productInfo: { name: string; price: number; productType: 'recurring' | 'one-time' },
  organizationId: string,
  academyId?: string
): Promise<Receipt> => {
  try {
    // Create initial debit receipt for the player
    const debitReceipt = await createDebitReceipt(
      playerRef,
      productRef,
      productInfo,
      organizationId,
      academyId
    );

    console.log('Created debit receipt for player:', playerId, 'Receipt ID:', debitReceipt.id);

    return debitReceipt;
  } catch (error) {
    console.error('Error creating player product receipts:', error);
    throw error;
  }
};

// Update a debit receipt (invoice) - allows editing product name, price, dates
export const updateDebitReceipt = async (
  userId: string,
  receiptId: string,
  updates: {
    productName?: string;
    amount?: number;
    invoiceDate?: Date;
    deadline?: Date;
  }
): Promise<void> => {
  try {
    console.log('📝 updateDebitReceipt: Updating receipt', receiptId, 'for user', userId);

    const receipt = await getReceiptById(userId, receiptId);
    if (!receipt) {
      throw new Error('Receipt not found');
    }

    if (receipt.type !== 'debit') {
      throw new Error('Can only edit debit receipts (invoices)');
    }

    // Build the update object
    const updateData: any = {};

    if (updates.amount !== undefined) {
      updateData.amount = updates.amount;
    }

    // Update product info if any product-related fields are changed
    if (updates.productName || updates.invoiceDate || updates.deadline || updates.amount !== undefined) {
      updateData.product = {
        ...receipt.product,
        ...(updates.productName && { name: updates.productName }),
        ...(updates.amount !== undefined && { price: updates.amount }),
        ...(updates.invoiceDate && { invoiceDate: Timestamp.fromDate(updates.invoiceDate) }),
        ...(updates.deadline && { deadline: Timestamp.fromDate(updates.deadline) })
      };
    }

    await updateReceipt(userId, receiptId, updateData);

    // Update aggregated stats (non-blocking)
    try {
      const invoiceDate = receipt.product?.invoiceDate?.toDate() || receipt.createdAt?.toDate() || new Date();
      const yyyy = invoiceDate.getFullYear();
      const mm = String(invoiceDate.getMonth() + 1).padStart(2, '0');
      const monthStr = `${yyyy}-${mm}`;
      const debitItemUpdates: any = {};
      if (updates.amount !== undefined) debitItemUpdates.amount = updates.amount;
      if (updates.productName) debitItemUpdates.productName = updates.productName;
      await updateDebitItem(receipt.organizationId, receiptId, monthStr, receipt.academyId, debitItemUpdates);

      // If amount changed, adjust stats delta using the explicit financialStatus field
      if (updates.amount !== undefined && updates.amount !== receipt.amount) {
        const diff = updates.amount - receipt.amount;
        if (receipt.financialStatus === 'overdue') {
          await updateOrgStats(receipt.organizationId, receipt.academyId, { overdue: diff } as any);
        } else if (receipt.financialStatus === 'pending') {
          await updateOrgStats(receipt.organizationId, receipt.academyId, { pending: diff } as any);
        }
        // 'settled' = already paid, amount change doesn't affect stats
      }
    } catch (statsErr) {
      console.error('Stats update error in updateDebitReceipt (non-blocking):', statsErr);
    }

    console.log('✅ updateDebitReceipt: Receipt updated successfully');
  } catch (error) {
    console.error('❌ updateDebitReceipt: Error updating receipt:', error);
    throw error;
  }
};

// Delete a debit receipt (invoice) and convert linked credits to available credits
export const deleteDebitReceiptWithCreditConversion = async (
  userId: string,
  receiptId: string,
  deletedBy: { name: string; userRef: DocumentReference }
): Promise<{ convertedCredits: number }> => {
  try {
    console.log('🗑️ deleteDebitReceiptWithCreditConversion: Starting deletion of receipt', receiptId);

    const receipt = await getReceiptById(userId, receiptId);
    if (!receipt) {
      throw new Error('Receipt not found');
    }

    if (receipt.type !== 'debit') {
      throw new Error('Can only delete debit receipts (invoices) with this function');
    }

    const batch = writeBatch(db);
    let convertedCredits = 0;

    // Get all credit receipts linked to this debit
    const userReceipts = await getReceiptsByUser(userId);
    const linkedCredits = userReceipts.filter(r =>
      r.type === 'credit' &&
      r.siblingReceiptRefs &&
      r.siblingReceiptRefs.some(ref => ref.id === receiptId)
    );

    console.log(`🔗 Found ${linkedCredits.length} linked credit receipts to unlink`);

    // Unlink each credit receipt from this debit (convert to available credit)
    for (const creditReceipt of linkedCredits) {
      const creditRef = doc(db, 'users', userId, 'receipts', creditReceipt.id);

      // Remove this debit from the credit's siblingReceiptRefs
      const updatedSiblingRefs = (creditReceipt.siblingReceiptRefs || [])
        .filter(ref => ref.id !== receiptId);

      // Revert credit status: 'active' if no remaining links, 'paid' if still linked to other debits
      const newCreditStatus = updatedSiblingRefs.length === 0 ? 'active' : 'paid';
      batch.update(creditRef, {
        siblingReceiptRefs: updatedSiblingRefs,
        status: newCreditStatus,
        description: `${creditReceipt.description || 'Payment'} (invoice deleted - converted to available credit)`,
        updatedAt: serverTimestamp()
      });

      convertedCredits += creditReceipt.amount;
      console.log(`💰 Unlinking credit receipt ${creditReceipt.id} - ${creditReceipt.amount} will become available credit`);
    }

    // Delete (or mark as deleted) the debit receipt
    const debitRef = doc(db, 'users', userId, 'receipts', receiptId);

    // Option: Mark as deleted instead of hard delete for audit trail
    batch.update(debitRef, {
      status: 'deleted',
      deletedAt: serverTimestamp(),
      deletedBy: deletedBy,
      siblingReceiptRefs: [], // Clear any links
      updatedAt: serverTimestamp()
    });

    await batch.commit();

    console.log(`✅ deleteDebitReceiptWithCreditConversion: Deleted receipt and converted ${convertedCredits} to available credits`);

    // Update aggregated stats (non-blocking)
    try {
      const invoiceDate = receipt.product?.invoiceDate?.toDate() || receipt.createdAt?.toDate() || new Date();
      const yyyy = invoiceDate.getFullYear();
      const mm = String(invoiceDate.getMonth() + 1).padStart(2, '0');
      const monthStr = `${yyyy}-${mm}`;
      await removeDebitItem(receipt.organizationId, receiptId, monthStr, receipt.academyId);
      // Use the explicit financialStatus field to determine which counter to decrement
      if (receipt.financialStatus === 'overdue') {
        await updateOrgStats(receipt.organizationId, receipt.academyId, { overdue: -receipt.amount } as any);
      } else if (receipt.financialStatus === 'pending') {
        await updateOrgStats(receipt.organizationId, receipt.academyId, { pending: -receipt.amount } as any);
      }
      // 'settled' = already paid, nothing to decrement from stats
      if (convertedCredits > 0) {
        await updateOrgStats(receipt.organizationId, receipt.academyId, { availableCredit: convertedCredits });
      }
    } catch (statsErr) {
      console.error('Stats update error in deleteDebitReceipt (non-blocking):', statsErr);
    }

    // Auto-apply freed credits to remaining outstanding debits
    if (linkedCredits.length > 0) {
      try {
        console.log('🔄 Auto-applying freed credits to remaining outstanding debits...');
        for (const creditReceipt of linkedCredits) {
          // Refetch the credit receipt to get updated state after batch commit
          const freshCredit = await getReceiptById(userId, creditReceipt.id);
          if (freshCredit && freshCredit.status !== 'deleted') {
            await autoApplyCreditsToOutstandingDebits(userId, receipt.organizationId, freshCredit);
          }
        }
        console.log('✅ Freed credits auto-applied to remaining debits');
      } catch (autoApplyError) {
        console.error('❌ Error auto-applying freed credits (non-blocking):', autoApplyError);
      }
    }

    // Update user balance after deletion
    try {
      await recalculateAndUpdateUserBalance(userId, receipt.organizationId);
      await recalculateAndUpdateUserOutstandingAndCredits(userId, receipt.organizationId);
      console.log('💰 User balance and outstanding/credits updated after receipt deletion');
    } catch (balanceError) {
      console.error('❌ Error updating user balance after receipt deletion:', balanceError);
    }

    return { convertedCredits };
  } catch (error) {
    console.error('❌ deleteDebitReceiptWithCreditConversion: Error:', error);
    throw error;
  }
};

/**
 * Get organization-wide financial stats with a single efficient query
 * This queries all receipts once instead of per-player
 *
 * Fixed issues:
 * - Properly handles partial payments by summing linked credit amounts
 * - Correctly tracks credit usage (linked vs unlinked)
 * - Improved overdue logic to check remaining unpaid balance
 */
export interface OrganizationFinancialStats {
  totalPlayersWithReceipts: number;
  paidUpCount: number;
  pendingCount: number;
  overdueCount: number;
  totalOutstanding: number;
  totalCredits: number;
}

export const getOrganizationFinancialStats = async (
  organizationId: string,
  academyId?: string
): Promise<OrganizationFinancialStats> => {
  try {
    console.log('📊 getOrganizationFinancialStats: Fetching stats for org:', organizationId);

    // Query all receipts for the organization in ONE query using collectionGroup
    let receiptsQuery = query(
      collectionGroup(db, 'receipts'),
      where('organizationId', '==', organizationId)
    );

    // Add academy filter if specified
    if (academyId) {
      receiptsQuery = query(
        collectionGroup(db, 'receipts'),
        where('organizationId', '==', organizationId),
        where('academyId', '==', academyId)
      );
    }

    const receiptsSnapshot = await getDocs(receiptsQuery);
    console.log(`📊 Fetched ${receiptsSnapshot.size} receipts in single query`);


    
    const now = new Date();

    // Build a map of all receipts by their ID for sibling lookup
    const receiptMap = new Map<string, { receipt: Receipt; userId: string; path: string }>();

    receiptsSnapshot.forEach(docSnap => {
      const receipt = docSnap.data() as Receipt;
      const userId = docSnap.ref.parent.parent?.id;
      if (userId) {
        receiptMap.set(docSnap.id, { receipt, userId, path: docSnap.ref.path });
      }
    });

    // Track stats per user
    const userStats = new Map<string, {
      outstanding: number;
      availableCredits: number;
      hasOverdue: boolean;
    }>();

    // Track which credits have been linked (to avoid double counting)
    const linkedCreditIds = new Set<string>();

    // First pass: Process debit receipts and calculate actual paid amounts
    receiptsSnapshot.forEach(docSnap => {
      const receipt = docSnap.data() as Receipt;
      const userId = docSnap.ref.parent.parent?.id;

      if (!userId) return;

      // Initialize user stats if not exists
      if (!userStats.has(userId)) {
        userStats.set(userId, { outstanding: 0, availableCredits: 0, hasOverdue: false });
      }

      const stats = userStats.get(userId)!;

      // Skip deleted receipts entirely
      if (receipt.status === 'deleted') return;

      // Process debit receipts
      if (receipt.type === 'debit') {
        // Skip fully paid/completed debits - they have no outstanding amount
        if (receipt.status === 'paid' || receipt.status === 'completed') return;

        const totalAmount = receipt.amount || receipt.product?.price || 0;

        // Calculate actual paid amount by summing linked credit receipts
        let paidAmount = 0;
        if (receipt.siblingReceiptRefs && receipt.siblingReceiptRefs.length > 0) {
          for (const siblingRef of receipt.siblingReceiptRefs) {
            const siblingData = receiptMap.get(siblingRef.id);

            if (siblingData && siblingData.receipt.type === 'credit' && siblingData.receipt.status !== 'deleted') {
              paidAmount += siblingData.receipt.amount || 0;
              // Mark this credit as linked so we don't count it as available
              linkedCreditIds.add(siblingRef.id);
            }
          }
        }

        const remainingAmount = Math.max(0, totalAmount - paidAmount);

        if (remainingAmount > 0) {
          stats.outstanding += remainingAmount;

          // Check if overdue - only if there's actually remaining unpaid balance
          const deadline = receipt.product?.deadline?.toDate?.();
          if (deadline && deadline < now) {
            stats.hasOverdue = true;
          }
        }
      }
    });

    // Second pass: Process credit receipts to find available (unlinked) credits
    receiptsSnapshot.forEach(docSnap => {
      const receipt = docSnap.data() as Receipt;
      const userId = docSnap.ref.parent.parent?.id;

      if (!userId) return;
      if (receipt.status === 'deleted') return;

      const stats = userStats.get(userId);
      if (!stats) return;

      if (receipt.type === 'credit') {
        // Check if this credit is linked to any debit
        const isLinked = linkedCreditIds.has(docSnap.id);

        if (!isLinked) {
          // This credit is available (not applied to any debit)
          stats.availableCredits += receipt.amount || 0;
        }
      }
    });

    // Calculate final stats
    let paidUpCount = 0;
    let pendingCount = 0;
    let overdueCount = 0;
    let totalOutstanding = 0;
    let totalCredits = 0;

    userStats.forEach((stats) => {
      totalOutstanding += stats.outstanding;
      totalCredits += stats.availableCredits;

      // Net balance: available credits minus outstanding debits
      const netBalance = stats.availableCredits - stats.outstanding;

      if (stats.outstanding === 0 || netBalance >= 0) {
        // User is paid up: no outstanding OR credits cover the outstanding
        paidUpCount++;
      } else if (stats.hasOverdue) {
        // User has overdue unpaid balance
        overdueCount++;
      } else {
        // User has outstanding but not overdue yet
        pendingCount++;
      }
    });

    const result: OrganizationFinancialStats = {
      totalPlayersWithReceipts: userStats.size,
      paidUpCount,
      pendingCount,
      overdueCount,
      totalOutstanding,
      totalCredits
    };

    console.log('📊 Organization financial stats:', JSON.stringify(result, null, 2));
    console.log(`📊 Stats breakdown: ${userStats.size} users with receipts, Paid Up: ${paidUpCount}, Pending: ${pendingCount}, Overdue: ${overdueCount}, Outstanding: ${totalOutstanding}, Credits: ${totalCredits}`);
    return result;

  } catch (error) {
    console.error('❌ getOrganizationFinancialStats: Error:', error);
    throw error;
  }
};

// ==================== FINANCIAL STATUS MANAGEMENT ====================

/**
 * Determine the financial status of a debit receipt based on payment and deadline
 * - settled: fully paid (status is 'completed')
 * - pending: not fully paid, deadline not yet passed
 * - overdue: not fully paid, deadline has passed
 */
export const determineReceiptFinancialStatus = (receipt: Receipt): 'settled' | 'pending' | 'overdue' => {
  // If receipt is fully paid (completed), it's settled
  if (receipt.status === 'completed') {
    return 'settled';
  }

  // If receipt is deleted, treat as settled (not counted)
  if (receipt.status === 'deleted') {
    return 'settled';
  }

  // Check if deadline has passed
  const now = new Date();
  const deadline = receipt.product?.deadline?.toDate?.();

  if (deadline && deadline < now) {
    return 'overdue';
  }

  return 'pending';
};

/**
 * Update the financial status of a specific receipt
 * Called when receipt is created or payment status changes
 */
export const updateReceiptFinancialStatus = async (
  userId: string,
  receiptId: string,
  newStatus?: 'settled' | 'pending' | 'overdue'
): Promise<void> => {
  try {
    const receipt = await getReceiptById(userId, receiptId);
    if (!receipt || receipt.type !== 'debit') return;

    const status = newStatus || determineReceiptFinancialStatus(receipt);

    // Only update if status changed
    if (receipt.financialStatus !== status) {
      const receiptRef = doc(db, 'users', userId, 'receipts', receiptId);
      await updateDoc(receiptRef, {
        financialStatus: status,
        updatedAt: serverTimestamp()
      });
      console.log(`📋 Receipt ${receiptId} financial status updated to: ${status}`);
    }
  } catch (error) {
    console.error('Error updating receipt financial status:', error);
    throw error;
  }
};

/**
 * Efficiently update user's financial status based on their receipts
 * Uses priority-based queries with early exit for efficiency
 * Priority: overdue > pending > settled
 */
export const updateUserFinancialStatus = async (
  userId: string,
  organizationId: string,
  excludeReceiptId?: string // Optional: exclude a specific receipt (e.g., the one just updated)
): Promise<'settled' | 'pending' | 'overdue'> => {
  try {
    console.log(`🔄 updateUserFinancialStatus: Checking status for user ${userId} in org ${organizationId}`);

    const userRef = doc(db, 'users', userId);
    const receiptsCollection = collection(userRef, 'receipts');

    // Step 1: Check for any overdue receipts (highest priority)
    let overdueQuery = query(
      receiptsCollection,
      where('organizationId', '==', organizationId),
      where('type', '==', 'debit'),
      where('financialStatus', '==', 'overdue')
    );

    const overdueSnapshot = await getDocs(overdueQuery);
    const hasOverdue = overdueSnapshot.docs.some(doc =>
      !excludeReceiptId || doc.id !== excludeReceiptId
    );

    if (hasOverdue) {
      await updateDoc(userRef, {
        [`financialStatus.${organizationId}`]: 'overdue',
        updatedAt: serverTimestamp()
      });
      console.log(`📋 User ${userId} financial status: overdue`);
      return 'overdue';
    }

    // Step 2: Check for any pending receipts (medium priority)
    let pendingQuery = query(
      receiptsCollection,
      where('organizationId', '==', organizationId),
      where('type', '==', 'debit'),
      where('financialStatus', '==', 'pending')
    );

    const pendingSnapshot = await getDocs(pendingQuery);
    const hasPending = pendingSnapshot.docs.some(doc =>
      !excludeReceiptId || doc.id !== excludeReceiptId
    );

    if (hasPending) {
      await updateDoc(userRef, {
        [`financialStatus.${organizationId}`]: 'pending',
        updatedAt: serverTimestamp()
      });
      console.log(`📋 User ${userId} financial status: pending`);
      return 'pending';
    }

    // Step 3: No overdue or pending, user is settled
    await updateDoc(userRef, {
      [`financialStatus.${organizationId}`]: 'settled',
      updatedAt: serverTimestamp()
    });
    console.log(`📋 User ${userId} financial status: settled`);
    return 'settled';

  } catch (error) {
    console.error('Error updating user financial status:', error);
    throw error;
  }
};

/**
 * Update receipt to overdue status (for cloud function)
 * Called when a receipt's deadline has passed
 */
export const markReceiptAsOverdue = async (
  userId: string,
  receiptId: string,
  organizationId: string
): Promise<void> => {
  try {
    const receiptRef = doc(db, 'users', userId, 'receipts', receiptId);

    // Read receipt to get amount and academyId for stats
    const receiptSnap = await getDoc(receiptRef);
    const receiptData = receiptSnap.exists() ? receiptSnap.data() as Receipt : null;

    await updateDoc(receiptRef, {
      financialStatus: 'overdue',
      updatedAt: serverTimestamp()
    });

    console.log(`⏰ Receipt ${receiptId} marked as overdue`);

    // Update user's financial status (will become overdue since we have an overdue receipt)
    await updateDoc(doc(db, 'users', userId), {
      [`financialStatus.${organizationId}`]: 'overdue',
      updatedAt: serverTimestamp()
    });

    // Update aggregated stats: move amount from pending to overdue (non-blocking)
    if (receiptData) {
      try {
        await updateOrgStats(organizationId, receiptData.academyId, {
          pending: -(receiptData.amount || 0),
          overdue: receiptData.amount || 0
        });
      } catch (statsErr) {
        console.error('Stats update error in markReceiptAsOverdue (non-blocking):', statsErr);
      }
    }

    console.log(`⏰ User ${userId} financial status updated to overdue`);
  } catch (error) {
    console.error('Error marking receipt as overdue:', error);
    throw error;
  }
};

/**
 * Get all pending receipts that should be marked as overdue
 * Used by the cloud function to efficiently query only relevant receipts
 */
export const getPendingReceiptsPassedDeadline = async (
  organizationId?: string
): Promise<{ userId: string; receiptId: string; organizationId: string }[]> => {
  try {
    const now = Timestamp.now();

    // Query all pending debit receipts across all users
    let receiptsQuery = query(
      collectionGroup(db, 'receipts'),
      where('type', '==', 'debit'),
      where('financialStatus', '==', 'pending'),
      where('product.deadline', '<=', now)
    );

    // Add organization filter if provided
    if (organizationId) {
      receiptsQuery = query(
        collectionGroup(db, 'receipts'),
        where('organizationId', '==', organizationId),
        where('type', '==', 'debit'),
        where('financialStatus', '==', 'pending'),
        where('product.deadline', '<=', now)
      );
    }

    const snapshot = await getDocs(receiptsQuery);

    const results: { userId: string; receiptId: string; organizationId: string }[] = [];

    snapshot.forEach(docSnap => {
      const userId = docSnap.ref.parent.parent?.id;
      if (userId) {
        const data = docSnap.data();
        results.push({
          userId,
          receiptId: docSnap.id,
          organizationId: data.organizationId
        });
      }
    });

    console.log(`📋 Found ${results.length} pending receipts passed deadline`);
    return results;
  } catch (error) {
    console.error('Error getting pending receipts passed deadline:', error);
    throw error;
  }
};

/**
 * Process overdue receipts - mark them as overdue and update user statuses
 * This is the main function called by the cloud function
 */
export const processOverdueReceipts = async (organizationId?: string): Promise<{
  processed: number;
  errors: number;
}> => {
  try {
    console.log('⏰ processOverdueReceipts: Starting...');

    const receiptsToUpdate = await getPendingReceiptsPassedDeadline(organizationId);

    let processed = 0;
    let errors = 0;

    // Group by user to batch updates
    const userReceipts = new Map<string, { receiptIds: string[]; organizationId: string }>();

    for (const { userId, receiptId, organizationId: orgId } of receiptsToUpdate) {
      const key = `${userId}:${orgId}`;
      if (!userReceipts.has(key)) {
        userReceipts.set(key, { receiptIds: [], organizationId: orgId });
      }
      userReceipts.get(key)!.receiptIds.push(receiptId);
    }

    // Process each user's receipts
    const userReceiptEntries = Array.from(userReceipts.entries());
    for (const [key, { receiptIds, organizationId: orgId }] of userReceiptEntries) {
      const userId = key.split(':')[0];

      try {
        // Update all receipts for this user to overdue
        for (const receiptId of receiptIds) {
          const receiptRef = doc(db, 'users', userId, 'receipts', receiptId);
          await updateDoc(receiptRef, {
            financialStatus: 'overdue',
            updatedAt: serverTimestamp()
          });
          processed++;
        }

        // Update user's financial status to overdue
        await updateDoc(doc(db, 'users', userId), {
          [`financialStatus.${orgId}`]: 'overdue',
          updatedAt: serverTimestamp()
        });

        console.log(`✅ Processed ${receiptIds.length} overdue receipts for user ${userId}`);
      } catch (error) {
        console.error(`❌ Error processing overdue receipts for user ${userId}:`, error);
        errors += receiptIds.length;
      }
    }

    console.log(`⏰ processOverdueReceipts complete. Processed: ${processed}, Errors: ${errors}`);
    return { processed, errors };
  } catch (error) {
    console.error('Error processing overdue receipts:', error);
    throw error;
  }
};

/**
 * Migration function to set initial financialStatus for all existing receipts and users
 * Run once to populate the new fields
 */
export const migrateFinancialStatuses = async (organizationId: string): Promise<{
  receiptsUpdated: number;
  usersUpdated: number;
  errors: number;
}> => {
  try {
    console.log('🔄 migrateFinancialStatuses: Starting migration for org:', organizationId);

    const results = {
      receiptsUpdated: 0,
      usersUpdated: 0,
      errors: 0
    };

    // Get all debit receipts for the organization
    const receiptsQuery = query(
      collectionGroup(db, 'receipts'),
      where('organizationId', '==', organizationId),
      where('type', '==', 'debit')
    );

    const receiptsSnapshot = await getDocs(receiptsQuery);
    console.log(`📋 Found ${receiptsSnapshot.size} debit receipts to process`);

    // Track users and their highest priority status
    const userStatuses = new Map<string, 'settled' | 'pending' | 'overdue'>();

    // Process each receipt
    for (const docSnap of receiptsSnapshot.docs) {
      try {
        const receipt = docSnap.data() as Receipt;
        const userId = docSnap.ref.parent.parent?.id;

        if (!userId) continue;

        // Skip deleted receipts
        if (receipt.status === 'deleted') continue;

        // Determine financial status
        const financialStatus = determineReceiptFinancialStatus(receipt);

        // Update receipt if status changed or not set
        if (receipt.financialStatus !== financialStatus) {
          await updateDoc(docSnap.ref, {
            financialStatus,
            updatedAt: serverTimestamp()
          });
          results.receiptsUpdated++;
        }

        // Track user's highest priority status
        const currentUserStatus = userStatuses.get(userId) || 'settled';
        const statusPriority = { 'settled': 0, 'pending': 1, 'overdue': 2 };

        if (statusPriority[financialStatus] > statusPriority[currentUserStatus]) {
          userStatuses.set(userId, financialStatus);
        }
      } catch (error) {
        console.error(`Error processing receipt ${docSnap.id}:`, error);
        results.errors++;
      }
    }

    // Update user statuses
    const userStatusEntries = Array.from(userStatuses.entries());
    for (const [userId, status] of userStatusEntries) {
      try {
        await updateDoc(doc(db, 'users', userId), {
          [`financialStatus.${organizationId}`]: status,
          updatedAt: serverTimestamp()
        });
        results.usersUpdated++;
      } catch (error) {
        console.error(`Error updating user ${userId} status:`, error);
        results.errors++;
      }
    }

    console.log(`🎉 Migration complete. Receipts: ${results.receiptsUpdated}, Users: ${results.usersUpdated}, Errors: ${results.errors}`);
    return results;
  } catch (error) {
    console.error('Error in migrateFinancialStatuses:', error);
    throw error;
  }
};

/**
 * Migration function to set financialStatus for ALL receipts and users across all organizations.
 * Run once from the browser console to backfill existing data.
 */
export const migrateAllFinancialStatuses = async (): Promise<{
  receiptsUpdated: number;
  usersUpdated: number;
  errors: number;
}> => {
  try {
    console.log('🔄 migrateAllFinancialStatuses: Starting global migration...');

    const results = {
      receiptsUpdated: 0,
      usersUpdated: 0,
      errors: 0
    };

    // Get ALL debit receipts across all organizations
    const receiptsQuery = query(
      collectionGroup(db, 'receipts'),
      where('type', '==', 'debit')
    );

    const receiptsSnapshot = await getDocs(receiptsQuery);
    console.log(`📋 Found ${receiptsSnapshot.size} debit receipts to process`);

    // Track per-user, per-org highest priority status
    // Key: `${userId}::${organizationId}`
    const userOrgStatuses = new Map<string, 'settled' | 'pending' | 'overdue'>();
    const statusPriority: Record<string, number> = { 'settled': 0, 'pending': 1, 'overdue': 2 };

    for (const docSnap of receiptsSnapshot.docs) {
      try {
        const receipt = docSnap.data() as Receipt;
        const userId = docSnap.ref.parent.parent?.id;

        if (!userId) continue;
        if (receipt.status === 'deleted') continue;

        const orgId = receipt.organizationId;
        if (!orgId) continue;

        const financialStatus = determineReceiptFinancialStatus(receipt);

        // Update receipt if status changed or not set
        if (receipt.financialStatus !== financialStatus) {
          await updateDoc(docSnap.ref, {
            financialStatus,
            updatedAt: serverTimestamp()
          });
          results.receiptsUpdated++;
        }

        // Track user's highest priority status per org
        const key = `${userId}::${orgId}`;
        const current = userOrgStatuses.get(key) || 'settled';

        if (statusPriority[financialStatus] > statusPriority[current]) {
          userOrgStatuses.set(key, financialStatus);
        }
      } catch (error) {
        console.error(`Error processing receipt ${docSnap.id}:`, error);
        results.errors++;
      }
    }

    // Update user statuses
    for (const [key, status] of Array.from(userOrgStatuses.entries())) {
      try {
        const [userId, orgId] = key.split('::');
        await updateDoc(doc(db, 'users', userId), {
          [`financialStatus.${orgId}`]: status,
          updatedAt: serverTimestamp()
        });
        results.usersUpdated++;
      } catch (error) {
        console.error(`Error updating user status for ${key}:`, error);
        results.errors++;
      }
    }

    console.log(`🎉 Global migration complete. Receipts: ${results.receiptsUpdated}, Users: ${results.usersUpdated}, Errors: ${results.errors}`);
    return results;
  } catch (error) {
    console.error('Error in migrateAllFinancialStatuses:', error);
    throw error;
  }
};

/**
 * Count debit receipts grouped by financialStatus for an organization.
 * Optionally filter by academyId.
 */
export const getReceiptFinancialStatusCounts = async (
  organizationId: string,
  academyId?: string
): Promise<{ settledCount: number; pendingCount: number; overdueCount: number }> => {
  try {
    const q = query(
      collectionGroup(db, 'receipts'),
      where('organizationId', '==', organizationId),
      where('type', '==', 'debit')
    );
    const snapshot = await getDocs(q);

    let settledCount = 0;
    let pendingCount = 0;
    let overdueCount = 0;

    snapshot.forEach(d => {
      const data = d.data();
      if (data.status === 'deleted') return;
      // Filter by academy in code to avoid composite index requirements
      if (academyId && data.academyId !== academyId) return;
      const fs = data.financialStatus;
      if (fs === 'overdue') overdueCount++;
      else if (fs === 'pending') pendingCount++;
      else if (fs === 'settled') settledCount++;
    });

    return { settledCount, pendingCount, overdueCount };
  } catch (error) {
    console.error('Error getting receipt financial status counts:', error);
    return { settledCount: 0, pendingCount: 0, overdueCount: 0 };
  }
};

/**
 * Delete all receipts for a player (user) within a specific organization.
 * Used when a player is deleted to cascade-remove their receipts.
 * Batch-deletes in chunks of 400 to stay within Firestore's 500 write limit.
 */
export const deletePlayerReceipts = async (
  userId: string,
  organizationId: string
): Promise<{ deletedCount: number }> => {
  try {
    console.log(`🗑️ deletePlayerReceipts: Deleting receipts for user ${userId} in org ${organizationId}`);

    const userRef = doc(db, 'users', userId);
    const receiptsRef = collection(userRef, 'receipts');
    const q = query(receiptsRef, where('organizationId', '==', organizationId));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log('🗑️ deletePlayerReceipts: No receipts found');
      return { deletedCount: 0 };
    }

    let deletedCount = 0;
    const docs = snapshot.docs;

    // Batch delete in chunks of 400
    for (let i = 0; i < docs.length; i += 400) {
      const chunk = docs.slice(i, i + 400);
      const batch = writeBatch(db);
      chunk.forEach(d => batch.delete(d.ref));
      await batch.commit();
      deletedCount += chunk.length;
    }

    console.log(`✅ deletePlayerReceipts: Deleted ${deletedCount} receipts`);
    return { deletedCount };
  } catch (error) {
    console.error('❌ deletePlayerReceipts: Error:', error);
    throw error;
  }
};

/**
 * Find and delete orphaned receipts — receipts belonging to users who are
 * no longer active players in the organization.
 * Recalculates org stats after cleanup.
 */
export const cleanOrphanedReceipts = async (
  organizationId: string,
  onProgress?: (msg: string) => void
): Promise<{ orphanedUsers: number; receiptsDeleted: number }> => {
  const log = (msg: string) => {
    console.log(msg);
    onProgress?.(msg);
  };

  try {
    log('🔍 Fetching players for organization...');
    const { getPlayersByOrganization } = await import('./playerService');
    const players = await getPlayersByOrganization(organizationId);
    const playerUserIds = new Set(players.map(p => p.userId).filter(Boolean));
    log(`👥 Found ${players.length} active players (${playerUserIds.size} with userId)`);

    log('🔍 Fetching all receipts for organization...');
    const receiptsQuery = query(
      collectionGroup(db, 'receipts'),
      where('organizationId', '==', organizationId)
    );
    const receiptsSnapshot = await getDocs(receiptsQuery);
    log(`🧾 Found ${receiptsSnapshot.size} total receipts`);

    // Group orphaned receipts by userId
    const orphanedByUser = new Map<string, typeof receiptsSnapshot.docs>();
    receiptsSnapshot.docs.forEach(d => {
      const userId = d.ref.parent.parent?.id;
      if (userId && !playerUserIds.has(userId)) {
        if (!orphanedByUser.has(userId)) {
          orphanedByUser.set(userId, []);
        }
        orphanedByUser.get(userId)!.push(d);
      }
    });

    const orphanedUsers = orphanedByUser.size;
    let receiptsDeleted = 0;

    if (orphanedUsers === 0) {
      log('✅ No orphaned receipts found');
    } else {
      log(`⚠️ Found ${orphanedUsers} orphaned user(s) with receipts`);

      for (const [userId, docs] of Array.from(orphanedByUser.entries())) {
        log(`  🗑️ Deleting ${docs.length} receipts for orphaned user ${userId}...`);
        for (let i = 0; i < docs.length; i += 400) {
          const chunk = docs.slice(i, i + 400);
          const batch = writeBatch(db);
          chunk.forEach(d => batch.delete(d.ref));
          await batch.commit();
          receiptsDeleted += chunk.length;
        }
      }
    }

    // Always recalculate stats — even with 0 orphans the counters may have drifted
    log(`📊 Recalculating stats from receipts...`);
    const { recalculateAllStats } = await import('./statsService');
    await recalculateAllStats(organizationId);

    log(`✅ Cleanup complete! Orphaned users: ${orphanedUsers}, Receipts deleted: ${receiptsDeleted}`);
    return { orphanedUsers, receiptsDeleted };
  } catch (error) {
    console.error('❌ cleanOrphanedReceipts: Error:', error);
    throw error;
  }
};
